import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem, type Encoding } from '@capacitor/filesystem';
import { LRUCache } from 'lru-cache';

import { generateRandomString } from '../utils.service';

import write_blob from 'capacitor-blob-writer';
import { CoreoRecordAttachment } from '../../types';
import { imageToImageProxy } from '../image.service';

export const clearDb = async () => {
  console.log('CLEARING DB TODO!!');
};

type FilePathComponent = string | number;
export const projectFilePath = (id: number, ...path: FilePathComponent[]) => `projects/${id}${path && path.length > 0 ? ('/' + path.join('/')) : ''}`;

export const uninstallProject = (id: number): Promise<void> => {
  return deleteProjectDir(id);
};

/**
 * LRU (Least Recently Used) Cache for media URLs
 * Evicts oldest entries when max size is reached
 * Max size: 150 entries
 */
const mediaCache = new LRUCache<number, string>({
  max: 150
});

export const mkdirSafe = async (path: string, directory: Directory = Directory.Data) => {
  try {
    await Filesystem.mkdir({
      path,
      recursive: true,
      directory
    });
  } catch (e) {
    if (Capacitor.isNativePlatform() && e.code !== 'OS-PLUG-FILE-0010') {
      throw e;
    }
  }
}

/******************************************************************************
 * PROJECT FILE UTILS
******************************************************************************/
export const projectFileExists = async (id: number, ...path: FilePathComponent[]): Promise<boolean> => {
  try {
    await Filesystem.stat({
      path: projectFilePath(id, ...path),
      directory: Directory.Data
    });
    return true;
  } catch (e) {
    return false;
  }
}

/** Size in bytes of a project file, or null when it does not exist */
export const projectFileSize = async (id: number, ...path: FilePathComponent[]): Promise<number | null> => {
  try {
    const stat = await Filesystem.stat({
      path: projectFilePath(id, ...path),
      directory: Directory.Data
    });
    return stat.size;
  } catch (e) {
    return null;
  }
}

export const createProjectDir = async (id: number, path: string = 'media'): Promise<void> => {
  try {
    await Filesystem.mkdir({
      path: projectFilePath(id, path),
      recursive: true,
      directory: Directory.Data
    });
  } catch (_e) {
  }
}

const deleteProjectDir = async (id: number): Promise<void> => {
  const dir = projectFilePath(id);
  try {
    await Filesystem.rmdir({
      path: dir,
      recursive: true,
      directory: Directory.Data
    });
  } catch (e) {
    console.warn(e);
  }
}

export const writeProjectFile = async (id: number, data: Blob, ...pathComponents: FilePathComponent[]): Promise<string> => {
  const path = projectFilePath(id, ...pathComponents);
  await write_blob({
    path,
    directory: Directory.Data,
    blob: data,
    fast_mode: true
  });
  return path;
}

/** Read a project file as a Blob; null when it is missing or unreadable. */
export const readProjectFileBlob = async (id: number, ...path: FilePathComponent[]): Promise<Blob | null> => {
  try {
    const { data } = await Filesystem.readFile({
      path: projectFilePath(id, ...path),
      directory: Directory.Data
    });
    if (typeof data !== 'string') {
      return data;
    }
    return new Blob([Uint8Array.from(atob(data), c => c.charCodeAt(0))]);
  } catch (e) {
    return null;
  }
}

/**
 * Read a small JSON file from the project directory; null when it is missing or unreadable.
 */
export const readProjectJsonFile = async <T = unknown>(id: number, ...path: FilePathComponent[]): Promise<T | null> => {
  try {
    const result = await Filesystem.readFile({
      path: projectFilePath(id, ...path),
      directory: Directory.Data,
      // Encoding.UTF8; the enum is type-only here as Vite's pre-bundle of the plugin has no runtime export for it
      encoding: 'utf8' as Encoding
    });
    const text = typeof result.data === 'string' ? result.data : await result.data.text();
    return JSON.parse(text) as T;
  } catch (e) {
    return null;
  }
}

const readProjectDir = async (id: number, ...path: FilePathComponent[]): Promise<string[]> => {
  try {
    const result = await Filesystem.readdir({
      path: projectFilePath(id, ...path),
      directory: Directory.Data
    });
    return result.files.map(s => s.name);
  } catch (e) {
    return [];
  }
}

export const readFileAsURL = async (path): Promise<string> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      const webResult = await Filesystem.readFile({
        directory: Directory.Data,
        path,
      });
      const url = URL.createObjectURL(webResult.data as any);
      return url;
    }

    const result = await Filesystem.getUri({
      directory: Directory.Data,
      path
    });
    return Capacitor.convertFileSrc(result.uri);
  } catch (e) {
    console.warn('File not found?', e, path);
    return null;
  }
}

const readProjectFileAsURL = async (id: number, ...path: FilePathComponent[]): Promise<string> => {
  return readFileAsURL(projectFilePath(id, ...path));
}

export const getProjectIcon = async (id: number): Promise<string> => {
  const iconPath = 'icon.png';
  const exists = await projectFileExists(id, iconPath);
  return exists ? readProjectFileAsURL(id, iconPath) : null;
}

export const getProjectMediaURL = async (projectId: number, mediaItemId: number): Promise<string> => {
  const cached = mediaCache.get(mediaItemId);
  if (typeof cached !== 'undefined') {
    return cached;
  }

  const exists = await projectFileExists(projectId, 'media', mediaItemId);
  if (exists) {
    const url = await readProjectFileAsURL(projectId, 'media', mediaItemId);
    if (url) {
      mediaCache.set(mediaItemId, url);
      return url;
    }
  }
  console.debug('Project media not found', mediaItemId);
  return null;
}

export const getCurrentProjectMediaURL = async (appId: number, mediaItemId: number): Promise<string> => {
  return getProjectMediaURL(appId, mediaItemId);
}

/**
 * Clear the media cache
 * Useful for memory management or when media files are deleted
 */
export const clearMediaCache = (): void => {
  mediaCache.clear();
}

/**
 * Get the current size of the media cache
 * Useful for monitoring memory usage
 */
export const getMediaCacheSize = (): number => {
  return mediaCache.size;
}

/**
 * Map a media MIME type to a file extension so Capacitor's native asset
 * handlers can infer Content-Type. Extensionless paths are reported as
 * text/html on iOS (WebViewAssetHandler.mimeTypeForExtension).
 */
export const extensionForMimeType = (mimeType?: string): string => {
  if (!mimeType) {
    return '';
  }
  const normalized = mimeType.toLowerCase();
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') {
    return '.jpg';
  }
  if (normalized === 'image/png') {
    return '.png';
  }
  if (normalized === 'image/heic' || normalized === 'image/heif') {
    return '.heic';
  }
  if (normalized === 'image/webp') {
    return '.webp';
  }
  if (normalized === 'image/gif') {
    return '.gif';
  }
  const subtype = normalized.split('/')[1];
  if (!subtype || subtype.includes('*')) {
    return '';
  }
  return `.${subtype}`;
};

// Takes file from cache and stores it in the top level of app storage
// note that it cleans up the original file
export const persistProjectFile = async (id: number, URI: string, mimeType?: string): Promise<string> => {
  const isNative = Capacitor.isNativePlatform();
  const filename = `${generateRandomString()}${extensionForMimeType(mimeType)}`;
  const path = projectFilePath(id, 'tmp', filename);

  if (!isNative) {
    const dataBlob = await (await fetch(URI)).blob();
    await writeProjectFile(id, dataBlob, 'tmp', filename);
  } else {
    await Filesystem.copy({
      from: URI,
      to: path,
      toDirectory: Directory.Data
    });
    await Filesystem.deleteFile({
      path: URI
    });
  }

  return path;
}

// const CACHE_VERSION = 1;
// const CACHE = `coreo-media-cache-${CACHE_VERSION}`;

export interface ConsolidateMediaItem {
  id: number;
  url: string;
};

export const consolidateMedia = async<T extends ConsolidateMediaItem>(projectId: number, folder: string, items: T[]): Promise<T[]> => {
  const currentStoredMedia = await readProjectDir(projectId, folder);
  return items.filter(c => !currentStoredMedia.includes(c.id.toString()));
}

export const convertBlobToBase64 = (blob: Blob): Promise<string | ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export const storeProjectIcon = async (id: number, icon: string) => {
  if (!icon) {
    return;
  }
  icon = imageToImageProxy(icon);

  let response: Response;

  try {
    response = await fetch(icon);
  } catch (error) {
    return;
  };

  const data = await response.blob();
  await writeProjectFile(id, data, 'icon.png');
};

export const resolveAttachmentUrl = async (attachment: CoreoRecordAttachment): Promise<string> => {
  if (!attachment) {
    return null;
  }
  const { url, fileLocation } = attachment;
  if (!(url || fileLocation)) {
    return null;
  }

  return fileLocation ? readFileAsURL(fileLocation) : (url ? imageToImageProxy(url) : null);
  // if(!Capacitor.isNativePlatform()){
  //   return url ? imageToImgix(url) : readFileAsURL(fileLocation);
  // }

  // if(fileLocation){
  //   return readFileAsURL(fileLocation);
  // }

  // if (url.startsWith('http')) {
  //   return imageToImgix(url);
  // } else {
  //   return readFileAsURL(url);
  // }
}


export class FileSystemService {
  public static instance: FileSystemService = new FileSystemService();

  public static readonly SYSTEM_DIR = 'system';
  public static readonly OFFLINE_MAPS_DIR = 'offlinemaps';

  private constructor() { }

  async init() {
    await mkdirSafe('system', Directory.Data);
    await mkdirSafe('system/offlinemaps', Directory.Data);
  }

  async writeSystemFile(data: Blob, ...pathComponents: FilePathComponent[]) {
    const path = ['system', ...pathComponents].join('/');
    await write_blob({
      path,
      directory: Directory.Data,
      blob: data,
      fast_mode: true
    });
    return path;
  }

  async initProjectFilesystem(id: number) {
    const directories = [
      'tmp',
      'media',
      'attachments',
      'maps',
      'items',
    ];
    try {
      // Create top-level folder first
      await mkdirSafe(projectFilePath(id));

      // Create subdirectories
      for (const directory of directories) {
        await mkdirSafe(projectFilePath(id, directory));
      }
    } catch (_e) {
      console.warn('Error creating project filesystem', _e);
    }
  }
}